// ===== Imports Start =====
import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  Mail, X, ChevronLeft, ChevronDown, Trash2,
  FolderPlus, ListPlus, Repeat, Clock, CheckCircle2, XCircle,
  ThumbsUp, ThumbsDown, RefreshCw, Send, MessageSquare, BellOff,
} from "lucide-react";
import axiosInstance from "../../API/interceptor";
// ===== Imports End =====

// ===== Types Start =====
interface Workspace {
  _id: string;
  product_code: number;
  name: string;
}

interface MailTypeChoice {
  value: string;
  label: string;
}

interface SavedPreference {
  product_code: number;
  workspace_name: string;
  mail_type: string;
  mail_type_label: string;
  opted_out_roles: string[];
}

interface MailPreferencesOffcanvasProps {
  isOpen: boolean;
  onClose: () => void;
}

// ===== Design Tokens Start =====
// Same RACI palette already used across the app (see RaciPopover.js) —
// reused here so this screen reads as part of the same product, not a bolt-on.
const RACI: Record<string, { key: string; label: string; color: string; bg: string }> = {
  responsible: { key: "R", label: "Responsible", color: "#16A34A", bg: "#F0FDF4" },
  accountable: { key: "A", label: "Accountable", color: "#2563EB", bg: "#EFF6FF" },
  consulted:   { key: "C", label: "Consulted",   color: "#D97706", bg: "#FFFBEB" },
  informed:    { key: "I", label: "Informed",    color: "#7C3AED", bg: "#F5F3FF" },
};
// Only these two can ever be muted — Responsible/Accountable always get notified,
// so there's no point showing them as toggleable options anywhere in this UI.
const MUTABLE_ROLES = ["consulted", "informed"];

// Same palette used in KanbanBoard.js's WorkspaceSingleSelect — reused for
// consistent workspace-color identity across the app.
const WORKSPACE_COLORS = [
  { bg: "#F3E8FF", color: "#7C3AED", border: "#DDD6FE" },
  { bg: "#E0F2FE", color: "#0284C7", border: "#BAE6FD" },
  { bg: "#FEF9C3", color: "#CA8A04", border: "#FDE68A" },
  { bg: "#DCFCE7", color: "#16A34A", border: "#BBF7D0" },
  { bg: "#FCE7F3", color: "#DB2777", border: "#F9A8D4" },
  { bg: "#FFF7ED", color: "#D97706", border: "#FDE68A" },
  { bg: "#FEF2F2", color: "#DC2626", border: "#FECACA" },
];

// One icon per mail type so the list scans instead of reading like a spec sheet
const MAIL_TYPE_ICON: Record<string, any> = {
  add_task:           FolderPlus,
  subtask_created:    ListPlus,
  occurrence_started: Repeat,
  extend_deadline:    Clock,
  deadline_approved:  CheckCircle2,
  deadline_rejected:  XCircle,
  task_completed:     CheckCircle2,
  task_approved:      ThumbsUp,
  task_rejected:      ThumbsDown,
  update_status:      RefreshCw,
  forword_task:       Send,
  task_comment:       MessageSquare,
};

// Simple on/off pill switch, reused for both the master toggle and each
// individual workspace row inside the Quick Mute sub-canvas.
const ToggleSwitch: React.FC<{ on: boolean; busy?: boolean; onClick: () => void; title?: string }> = ({ on, busy, onClick, title }) => (
  <button
    onClick={onClick}
    disabled={busy}
    title={title}
    className="relative w-9 h-5 rounded-full transition-colors shrink-0 disabled:cursor-wait"
    style={{ background: on ? "#7C3AED" : "#CBD5E1" }}
  >
    <span
      className="absolute top-[2px] w-4 h-4 rounded-full bg-white transition-all flex items-center justify-center"
      style={{ left: on ? "18px" : "2px" }}
    >
      {busy && (
        <span className="w-2.5 h-2.5 rounded-full border-2 border-slate-200 border-t-violet-600 animate-spin" />
      )}
    </span>
  </button>
);
// ===== Design Tokens End =====

// ===== MailPreferencesOffcanvas Component Start =====
const MailPreferencesOffcanvas: React.FC<MailPreferencesOffcanvasProps> = ({ isOpen, onClose }) => {
  // ===== State Start =====
  const [step, setStep] = useState<"workspace" | "mailTypes">("workspace");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [mailTypes, setMailTypes]   = useState<MailTypeChoice[]>([]);
  const [selectedWorkspace, setSelectedWorkspace] = useState<Workspace | null>(null);

  // { [mail_type]: Set of opted-out roles } — for the CURRENTLY selected workspace
  const [selections, setSelections] = useState<Record<string, Set<string>>>({});

  // All saved preferences across every workspace, for the summary list
  const [allPreferences, setAllPreferences] = useState<SavedPreference[]>([]);
  const [saving, setSaving] = useState<string | null>(null); // mail_type currently saving

  // ===== Quick Mute State Start =====
  // Roles the Quick Mute sub-canvas currently targets — ONLY informed/consulted ever go in here.
  // Its size > 0 is also what controls whether the sub-canvas is open.
  const [quickRoles, setQuickRoles] = useState<Set<string>>(new Set());
  const [bulkSavingCode, setBulkSavingCode] = useState<number | null>(null); // product_code currently toggling
  // ===== Quick Mute State End =====

  // Product codes whose "Your muted notifications" group is expanded past the
  // collapsed preview (see MUTED_PREVIEW_COUNT below).
  const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set());
  // product_code of the workspace group currently being bulk-removed
  const [removingGroupCode, setRemovingGroupCode] = useState<number | null>(null);
  // ===== State End =====

  // ===== Reset on Open Start =====
  useEffect(() => {
    if (!isOpen) return;
    setStep("workspace");
    setSelectedWorkspace(null);
    setSelections({});
    setQuickRoles(new Set());
    setExpandedGroups(new Set());
    fetchInitialData();
  }, [isOpen]);
  // ===== Reset on Open End =====

  // ===== ESC Key Handler Start =====
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    if (isOpen) document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [isOpen]);
  // ===== ESC Key Handler End =====

  // ===== Fetch Workspaces + Meta + Saved Preferences Start =====
  const fetchInitialData = async () => {
    setLoading(true);
    setError(false);
    try {
      const [productsRes, metaRes, prefRes] = await Promise.all([
        axiosInstance.get("/task_assignment/get_products"),
        axiosInstance.get("/task_assignment/mail_preferences/meta"),
        axiosInstance.get("/task_assignment/mail_preferences"),
      ]);

      setWorkspaces(productsRes?.data?.products || []);
      setMailTypes(metaRes?.data?.mail_types || []);
      setAllPreferences(prefRes?.data?.preferences || []);
    } catch (err) {
      console.error("Error loading mail preferences:", err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };
  // ===== Fetch Workspaces + Meta + Saved Preferences End =====

  // ===== Select a Workspace Start =====
  const openWorkspace = (ws: Workspace) => {
    setSelectedWorkspace(ws);
    setQuickRoles(new Set()); // closes the Quick Mute sub-canvas as we move into detail view

    // Pre-fill toggles from already-saved preferences for this workspace
    const preset: Record<string, Set<string>> = {};
    allPreferences
      .filter((p) => p.product_code === ws.product_code)
      .forEach((p) => {
        preset[p.mail_type] = new Set(p.opted_out_roles);
      });
    setSelections(preset);
    setStep("mailTypes");
  };
  // ===== Select a Workspace End =====

  // ===== Quick Mute Helpers Start =====
  // "ALL" = both informed + consulted checked together. Never touches R/A.
  const toggleQuickRole = (role: "all" | "informed" | "consulted") => {
    setQuickRoles((prev) => {
      const next = new Set(prev);
      if (role === "all") {
        const bothOn = next.has("informed") && next.has("consulted");
        if (bothOn) { next.delete("informed"); next.delete("consulted"); }
        else { next.add("informed"); next.add("consulted"); }
      } else {
        if (next.has(role)) next.delete(role);
        else next.add(role);
      }
      return next;
    });
  };

  // A workspace's toggle reads ON only when EVERY mail type already has ALL
  // of the currently-checked quick-roles muted for it. This is re-evaluated
  // fresh every time quickRoles changes — it is NOT a saved on/off flag, so
  // switching from "Consulted" to "Informed" can flip a toggle's reading
  // even though nothing was clicked in between.
  const isWorkspaceFullyMuted = (productCode: number): boolean => {
    if (quickRoles.size === 0 || mailTypes.length === 0) return false;
    return mailTypes.every((mt) => {
      const pref = allPreferences.find((p) => p.product_code === productCode && p.mail_type === mt.value);
      const muted = new Set(pref?.opted_out_roles || []);
      return Array.from(quickRoles).every((r) => muted.has(r));
    });
  };

  const toggleBulkMute = async (ws: Workspace) => {
    if (quickRoles.size === 0) return;
    const enabling = !isWorkspaceFullyMuted(ws.product_code);

    setBulkSavingCode(ws.product_code);
    try {
      await axiosInstance.post("/task_assignment/mail_preferences/bulk", {
        product_code: ws.product_code,
        roles:        Array.from(quickRoles),
        enabled:      enabling,
      });
      const prefRes = await axiosInstance.get("/task_assignment/mail_preferences");
      setAllPreferences(prefRes?.data?.preferences || []);
    } catch (err) {
      console.error("Error updating bulk mute:", err);
    } finally {
      setBulkSavingCode(null);
    }
  };

  // ===== Quick Mute Helpers End =====

  // ===== Toggle a Role Pill + Save Start =====
  const toggleRole = async (mailType: string, role: string) => {
    if (!selectedWorkspace) return;

    const current   = selections[mailType] || new Set<string>();
    const nextRoles = new Set(current);
    if (nextRoles.has(role)) nextRoles.delete(role);
    else nextRoles.add(role);

    setSelections((prev) => ({ ...prev, [mailType]: nextRoles }));
    setSaving(mailType);
    try {
      await axiosInstance.post("/task_assignment/mail_preferences", {
        product_code:    selectedWorkspace.product_code,
        mail_type:       mailType,
        opted_out_roles: Array.from(nextRoles),
      });

      // Refresh the summary data in the background
      const prefRes = await axiosInstance.get("/task_assignment/mail_preferences");
      setAllPreferences(prefRes?.data?.preferences || []);
    } catch (err) {
      console.error("Error saving mail preference:", err);
    } finally {
      setSaving(null);
    }
  };
  // ===== Toggle a Role Pill + Save End =====

  // ===== Remove a Whole Workspace Group Start =====
  const removeGroup = async (group: { product_code: number; prefs: SavedPreference[] }) => {
    setRemovingGroupCode(group.product_code);
    try {
      await Promise.all(
        group.prefs.map((p) =>
          axiosInstance.delete("/task_assignment/mail_preferences", {
            data: { product_code: p.product_code, mail_type: p.mail_type },
          })
        )
      );
      setAllPreferences((prev) => prev.filter((p) => p.product_code !== group.product_code));
      if (selectedWorkspace?.product_code === group.product_code) {
        setSelections({});
      }
    } catch (err) {
      console.error("Error removing workspace mail preferences:", err);
    } finally {
      setRemovingGroupCode(null);
    }
  };
  // ===== Remove a Whole Workspace Group End =====

  // ===== Group Muted Preferences by Workspace Start =====
  // Preferences arrive as a flat list across every workspace — group them so
  // the summary can show one collapsed card per workspace instead of a wall
  // of rows.
  const mutedGroups = useMemo(() => {
    const byCode = new Map<number, { product_code: number; workspace_name: string; prefs: SavedPreference[] }>();
    allPreferences.forEach((p) => {
      const existing = byCode.get(p.product_code);
      if (existing) existing.prefs.push(p);
      else byCode.set(p.product_code, { product_code: p.product_code, workspace_name: p.workspace_name, prefs: [p] });
    });
    return Array.from(byCode.values());
  }, [allPreferences]);

  const toggleGroupExpanded = (productCode: number) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(productCode)) next.delete(productCode);
      else next.add(productCode);
      return next;
    });
  };
  // ===== Group Muted Preferences by Workspace End =====

  const totalMuted = allPreferences.length;

  if (!isOpen) return null;

  // ===== Render Start =====
  return createPortal(
    <>
      {/* ===== Overlay Start ===== */}
      <div onClick={onClose} className="fixed inset-0 bg-black/50 z-[99998]" />
      {/* ===== Overlay End ===== */}

      {/* ===== Offcanvas Start ===== */}
      <div className="fixed top-0 right-0 bottom-0 w-[70vw] bg-white shadow-[-4px_0_24px_rgba(0,0,0,0.15)] z-[99999] flex flex-col translate-x-0 transition-transform duration-300 ease-in-out">

        {/* ===== Gradient Header Start ===== */}
        <div className="px-6 py-5 bg-gradient-to-br from-[#7C3AED] to-[#6D28D9] shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {step === "mailTypes" && (
                <button
                  onClick={() => setStep("workspace")}
                  className="w-8 h-8 rounded-full flex items-center justify-center bg-white/15 border border-white/25 text-white hover:bg-white/25 transition-colors shrink-0"
                  title="Back to workspaces"
                >
                  <ChevronLeft size={16} />
                </button>
              )}
              <div className="w-10 h-10 rounded-full flex items-center justify-center bg-white/20 border-2 border-white/35 shrink-0">
                <Mail size={18} className="text-white" />
              </div>
              <div>
                <div className="text-base font-black text-white leading-tight">
                  Email Preferences
                </div>
                <div className="text-[11px] text-white/65 mt-0.5">
                  {step === "workspace"
                    ? "Pick a workspace to opt out of task emails, role by role"
                    : <>Opting out for <span className="font-semibold text-white/85">{selectedWorkspace?.name}</span></>}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {totalMuted > 0 && (
                <div className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white/18 border border-white/25">
                  <BellOff size={12} className="text-white/85" />
                  <span className="text-xs font-bold text-white">{totalMuted} opted out</span>
                </div>
              )}
              <button
                onClick={onClose}
                className="w-9 h-9 rounded-full flex items-center justify-center bg-white/15 border border-white/25 text-white hover:bg-white/25 transition-colors"
              >
                <X size={16} />
              </button>
            </div>
          </div>
        </div>
        {/* ===== Gradient Header End ===== */}

        {/* ===== Body Start ===== */}
        <div className="flex-1 overflow-y-auto px-6 py-5 min-h-[300px]">
          {loading && (
            <div className="text-center py-14">
              <div className="w-8 h-8 mx-auto rounded-full border-[3px] border-[#EDE9FE] border-t-[#7C3AED] animate-spin" />
              <p className="text-[#94A3B8] mt-3 text-sm">Loading your preferences...</p>
            </div>
          )}

          {!loading && error && (
            <div className="text-center py-14">
              <XCircle size={36} className="text-[#EF4444] mx-auto" />
              <p className="text-[#EF4444] mt-2.5 text-sm font-medium">Couldn't load email preferences</p>
              <p className="text-[#94A3B8] text-xs mt-1">Please close this and try again</p>
            </div>
          )}

          {/* ===== Step 1: Quick Mute Checkboxes ===== */}
          {!loading && !error && step === "workspace" && (
            <>
              <div className="mb-4 px-3.5 py-3 rounded-xl border border-[#E8ECF0] bg-[#F8FAFC]">
                <div className="text-[12px] font-semibold text-slate-600 mb-2">Quick opt-out</div>
                <div className="flex items-center gap-4 flex-wrap">
                  {(["all", "informed", "consulted"] as const).map((role) => {
                    const checked =
                      role === "all"
                        ? quickRoles.has("informed") && quickRoles.has("consulted")
                        : quickRoles.has(role);
                    const roleColor = role === "informed" ? RACI.informed.color : role === "consulted" ? RACI.consulted.color : "#334155";
                    return (
                      <label key={role} className="flex items-center gap-2 cursor-pointer select-none">
                        <span
                          onClick={() => toggleQuickRole(role)}
                          className="w-[18px] h-[18px] rounded flex items-center justify-center border-2 transition-all shrink-0"
                          style={checked ? { background: roleColor, borderColor: roleColor } : { borderColor: "#CBD5E1", background: "#fff" }}
                        >
                          {checked && <div className="w-2 h-2 rounded-sm bg-white" />}
                        </span>
                        <span
                          onClick={() => toggleQuickRole(role)}
                          className="text-[13px] font-semibold capitalize"
                          style={{ color: checked ? roleColor : "#64748B" }}
                        >
                          {role === "all" ? "ALL" : RACI[role].label}
                        </span>
                      </label>
                    );
                  })}
                </div>
                <p className="text-[10.5px] text-slate-400 mt-2 mb-0">
                  Responsible &amp; Accountable can't be opted out here — they own the task.
                </p>
              </div>

              {/* ===== Summary: Everything Muted, Grouped by Workspace ===== */}
              {mutedGroups.length > 0 && (
                <div className="mt-7">
                  <h6 className="font-bold text-[13px] mb-2.5 text-slate-700 flex items-center gap-1.5">
                    <BellOff size={13} className="text-slate-400" />
                    Your opted-out notifications
                  </h6>
                  <div className="flex flex-col gap-3">
                    {mutedGroups.map((group, i) => {
                      const pal = WORKSPACE_COLORS[i % WORKSPACE_COLORS.length];
                      const expanded = expandedGroups.has(group.product_code);
                      return (
                        <div
                          key={group.product_code}
                          className="rounded-xl border border-[#E8ECF0] bg-[#F8FAFC] overflow-hidden"
                        >
                          <div
                            onClick={() => toggleGroupExpanded(group.product_code)}
                            className="w-full flex items-center justify-between gap-2 px-3.5 py-2.5 cursor-pointer"
                          >
                            <span
                              className="inline-flex items-center text-[12px] font-semibold px-2.5 py-1 rounded-full border truncate"
                              style={{ background: pal.bg, color: pal.color, borderColor: pal.border }}
                            >
                              {group.workspace_name}
                            </span>
                            <span className="flex items-center gap-2 shrink-0">
                              <span className="text-[11px] text-slate-400 font-medium">
                                {group.prefs.length} opted out
                              </span>
                              <button
                                onClick={(e) => { e.stopPropagation(); removeGroup(group); }}
                                disabled={removingGroupCode === group.product_code}
                                title="Remove all opted-out preferences for this workspace"
                                className="w-6 h-6 rounded-lg flex items-center justify-center text-[#EF4444] hover:bg-[#FEF2F2] transition-colors disabled:cursor-wait disabled:hover:bg-transparent"
                              >
                                {removingGroupCode === group.product_code
                                  ? <span className="w-3 h-3 rounded-full border-2 border-red-200 border-t-[#EF4444] animate-spin" />
                                  : <Trash2 size={13} />}
                              </button>
                              <ChevronDown
                                size={14}
                                className={`text-slate-400 transition-transform ${expanded ? "rotate-180" : ""}`}
                              />
                            </span>
                          </div>

                          {expanded && (
                            <div className="flex flex-col divide-y divide-[#F1F5F9] border-t border-[#E8ECF0]">
                              {group.prefs.map((p) => (
                                <div
                                  key={`${p.product_code}-${p.mail_type}`}
                                  className="flex items-center gap-3 px-3.5 py-2.5"
                                >
                                  <div className="flex-1 min-w-0">
                                    <div className="text-[12.5px] font-semibold text-slate-700">
                                      {p.mail_type_label}
                                    </div>
                                    <div className="flex items-center gap-1 mt-1 flex-wrap">
                                      {p.opted_out_roles.map((r) => (
                                        <span
                                          key={r}
                                          className="px-2 py-[2px] rounded-full text-[9.5px] font-bold"
                                          style={{ background: RACI[r]?.bg, color: RACI[r]?.color }}
                                        >
                                          {RACI[r]?.label || r}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}

          {/* ===== Step 2: Mail Types + RACI Toggle Pills ===== */}
          {!loading && !error && step === "mailTypes" && selectedWorkspace && (
            <div>
              <div className="flex items-start gap-2 mb-4 px-3.5 py-2.5 rounded-xl bg-[#F5F3FF] border border-[#DDD6FE]">
                <BellOff size={14} className="text-[#7C3AED] mt-0.5 shrink-0" />
                <p className="text-[12px] text-[#6D28D9] leading-snug m-0">
                  Tap a role to opt out of that email when you hold <strong>only</strong> that role on a task.
                  Hold another, opted-in role on the same task and you'll still get notified.
                </p>
              </div>

              <div className="flex flex-col gap-2">
                {mailTypes.map((mt) => {
                  const Icon = MAIL_TYPE_ICON[mt.value] || Mail;
                  const activeRoles = selections[mt.value] || new Set<string>();
                  const isSaving = saving === mt.value;
                  return (
                    <div
                      key={mt.value}
                      className="flex items-center gap-3 px-3.5 py-3 rounded-xl border border-[#E8ECF0] bg-white hover:border-[#DDD6FE] transition-colors"
                    >
                      <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-[#F8FAFC] border border-[#E8ECF0] shrink-0">
                        <Icon size={15} className="text-[#64748B]" />
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                          {mt.label}
                          {isSaving && <span className="text-[10px] font-normal text-[#94A3B8]">saving...</span>}
                        </div>
                      </div>

                      <div className="flex items-center gap-1.5 shrink-0">
                        {MUTABLE_ROLES.map((role) => {
                          const r = RACI[role];
                          const active = activeRoles.has(role);
                          return (
                            <button
                              key={role}
                              onClick={() => toggleRole(mt.value, role)}
                              title={`${active ? "Opt back in" : "Opt out"} when ${r.label} only`}
                              className="w-8 h-8 rounded-lg flex items-center justify-center text-[11px] font-black transition-all duration-150"
                              style={
                                active
                                  ? { background: r.color, color: "#fff", boxShadow: `0 3px 8px ${r.color}55` }
                                  : { background: r.bg, color: r.color, border: `1.5px solid ${r.color}33` }
                              }
                            >
                              {r.key}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
              {/* Role legend */}
              {/* Role legend */}
              <div className="flex flex-wrap gap-3 mt-4 px-1">
                {MUTABLE_ROLES.map((role) => (
                  <span key={role} className="flex items-center gap-1.5 text-[11px] text-slate-500">
                    <span
                      className="w-4 h-4 rounded flex items-center justify-center text-[8px] font-black text-white"
                      style={{ background: RACI[role].color }}
                    >
                      {RACI[role].key}
                    </span>
                    {RACI[role].label}
                  </span>
                ))}
              </div>
            </div>
          )}

        </div>
        {/* ===== Body End ===== */}
      </div>
      {/* ===== Offcanvas End ===== */}

      {/* ===== Quick Mute Sub-Canvas Start ===== */}
      {/* A second offcanvas, sliding in from the right over the first — opens the moment any Quick Mute role is checked. */}
      {step === "workspace" && quickRoles.size > 0 && (
        <>
          <div onClick={() => setQuickRoles(new Set())} className="fixed inset-0 bg-black/55 z-[100000]" />

          <div className="fixed top-0 right-0 bottom-0 w-[620px] max-w-[90vw] bg-white shadow-[-4px_0_24px_rgba(0,0,0,0.25)] z-[100001] flex flex-col translate-x-0 transition-transform duration-300 ease-in-out">

            {/* Gradient header */}
            <div className="px-5 py-4 bg-gradient-to-br from-[#7C3AED] to-[#6D28D9] shrink-0">
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <div className="text-[15px] font-black text-white leading-tight">Quick Opt-Out Workspaces</div>
                  <div className="text-[11px] text-white/70 mt-0.5 truncate">
                    Opting out: {Array.from(quickRoles).map((r) => RACI[r]?.label).join(" + ")}
                  </div>
                </div>
                <button
                  onClick={() => setQuickRoles(new Set())}
                  className="w-8 h-8 rounded-full flex items-center justify-center bg-white/15 border border-white/25 text-white hover:bg-white/25 transition-colors shrink-0"
                >
                  <X size={15} />
                </button>
              </div>
            </div>

            {/* Individual workspace toggles */}
            <div className="flex-1 overflow-y-auto divide-y divide-[#F1F5F9]">
              {workspaces.map((ws, i) => {
                const pal = WORKSPACE_COLORS[i % WORKSPACE_COLORS.length];
                const on = isWorkspaceFullyMuted(ws.product_code);
                const isBusy = bulkSavingCode === ws.product_code;
                return (
                  <div key={ws._id} className="flex items-center justify-between gap-3 px-5 py-3">
                    <button
                      onClick={() => openWorkspace(ws)}
                      className="inline-flex items-center text-[12.5px] font-semibold px-2.5 py-1 rounded-full border truncate"
                      style={{ background: pal.bg, color: pal.color, borderColor: pal.border }}
                    >
                      {ws.name}
                    </button>

                    <ToggleSwitch
                      on={on}
                      busy={isBusy}
                      onClick={() => toggleBulkMute(ws)}
                      title={on ? "Opt back in to all mail types" : "Opt out of all mail types"}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
      {/* ===== Quick Mute Sub-Canvas End ===== */}
    </>,
    document.body
  );
  // ===== Render End =====
};
// ===== MailPreferencesOffcanvas Component End =====

export default MailPreferencesOffcanvas;